import { useState } from 'react';
import { ArrowLeft, Pill, Activity, FlaskConical, AlertCircle, CheckCircle, XCircle, Calendar, FileText, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

interface MedicationAdherenceData {
  overall: number;
  scheduledDoses: number;
  takenDoses: number;
  missedDoses: number;
  medications: {
    name: string;
    dosage: string;
    frequency: string;
    scheduled: number;
    taken: number;
    missedDates: string[];
  }[];
}

interface ExerciseAdherenceData {
  overall: number;
  assignedExercises: number;
  completedExercises: number;
  exercises: {
    name: string;
    type: string;
    scheduledPerWeek: number;
    completedThisWeek: number;
    lastCompleted: string | null;
    missedDates: string[];
  }[];
}

interface HomeTestAdherenceData {
  overall: number;
  scheduledTests: number;
  completedTests: number;
  tests: {
    testName: string;
    frequency: string;
    lastCompleted: string | null;
    nextScheduled: string;
    missedCount: number;
    missedDates: string[];
  }[];
}

interface PatientAdherenceDetail {
  patientId: string;
  patientName: string;
  age: number;
  cohort: string;
  overallAdherence: number;
  medication: MedicationAdherenceData;
  exercise: ExerciseAdherenceData;
  homeTest: HomeTestAdherenceData;
}

interface DoctorLowAdherenceDetailProps {
  patient: PatientAdherenceDetail;
  onBack: () => void;
  onMarkReviewed: (patientId: string, notes: string) => void;
}

export function DoctorLowAdherenceDetail({ 
  patient, 
  onBack, 
  onMarkReviewed 
}: DoctorLowAdherenceDetailProps) {
  const [doctorNotes, setDoctorNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 85) return 'text-[#14CAF4]';
    if (adherence >= 70) return 'text-[#F07D13]';
    return 'text-[#F07D13]';
  };

  const getAdherenceBgColor = (adherence: number) => {
    if (adherence >= 85) return 'bg-[#14CAF4]/10';
    if (adherence >= 70) return 'bg-[#F07D13]/10';
    return 'bg-[#F07D13]/10';
  };

  const getAdherenceBorderColor = (adherence: number) => {
    if (adherence >= 85) return 'border-[#14CAF4]';
    if (adherence >= 70) return 'border-[#F07D13]';
    return 'border-[#F07D13]';
  };

  const handleMarkAsReviewed = () => {
    if (!doctorNotes.trim()) {
      toast.error('Please add notes before marking as reviewed');
      return;
    }

    setIsSaving(true);
    setTimeout(() => {
      onMarkReviewed(patient.patientId, doctorNotes);
      toast.success('Patient marked as reviewed', {
        description: 'Notes saved successfully. Patient removed from low adherence list.',
      });
      setIsSaving(false);
      onBack();
    }, 500);
  };

  const handleSaveNotes = () => {
    if (!doctorNotes.trim()) {
      toast.error('Please enter some notes');
      return;
    }

    setIsSaving(true);
    setTimeout(() => {
      toast.success('Notes saved successfully', {
        description: 'Your notes have been added to the patient record',
      });
      setIsSaving(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8]">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={onBack} size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Back to Low Adherence List</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div>
                <h1 className="text-primary">Adherence Details</h1>
                <p className="text-muted-foreground">
                  {patient.patientName} • {patient.age} years • {patient.cohort}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Badge 
                variant="outline" 
                className={`${getAdherenceBgColor(patient.overallAdherence)} ${getAdherenceColor(patient.overallAdherence)} ${getAdherenceBorderColor(patient.overallAdherence)}`}
              >
                {patient.overallAdherence}% Overall Adherence
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={`border-2 ${patient.medication.overall < 85 ? 'border-[#F07D13]/30' : 'border-[#14CAF4]/30'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-[#FEEBDA] flex items-center justify-center">
                  <Pill className="w-6 h-6 text-[#F07D13]" />
                </div>
                <h2 className={getAdherenceColor(patient.medication.overall)}>
                  {patient.medication.overall}%
                </h2>
              </div>
              <h3 className="text-primary mb-1">Medication Adherence</h3>
              <p className="text-muted-foreground">
                {patient.medication.takenDoses} of {patient.medication.scheduledDoses} doses taken
              </p>
            </CardContent>
          </Card>

          <Card className={`border-2 ${patient.exercise.overall < 85 ? 'border-[#F07D13]/30' : 'border-[#14CAF4]/30'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-[#D4F0F8] flex items-center justify-center">
                  <Activity className="w-6 h-6 text-[#2762AC]" />
                </div>
                <h2 className={getAdherenceColor(patient.exercise.overall)}>
                  {patient.exercise.overall}%
                </h2>
              </div>
              <h3 className="text-primary mb-1">Exercise Adherence</h3>
              <p className="text-muted-foreground">
                {patient.exercise.completedExercises} of {patient.exercise.assignedExercises} sessions completed
              </p>
            </CardContent>
          </Card>

          <Card className={`border-2 ${patient.homeTest.overall < 85 ? 'border-[#F07D13]/30' : 'border-[#14CAF4]/30'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-full bg-[#FEEBDA] flex items-center justify-center">
                  <FlaskConical className="w-6 h-6 text-[#F07D13]" />
                </div>
                <h2 className={getAdherenceColor(patient.homeTest.overall)}>
                  {patient.homeTest.overall}%
                </h2>
              </div>
              <h3 className="text-primary mb-1">Home Test Adherence</h3>
              <p className="text-muted-foreground">
                {patient.homeTest.completedTests} of {patient.homeTest.scheduledTests} tests completed
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Medication Adherence Details */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <Pill className="w-5 h-5" />
              Medication Adherence Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-[#e5e5e3] rounded-xl">
                <p className="text-muted-foreground">Scheduled Doses</p>
                <h3 className="text-primary">{patient.medication.scheduledDoses}</h3>
              </div>
              <div className="p-4 bg-[#D4F0F8] rounded-xl">
                <p className="text-muted-foreground">Taken Doses</p>
                <h3 className="text-[#14CAF4]">{patient.medication.takenDoses}</h3>
              </div>
              <div className="p-4 bg-[#FEEBDA] rounded-xl">
                <p className="text-muted-foreground">Missed Doses</p>
                <h3 className="text-[#F07D13]">{patient.medication.missedDoses}</h3>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="text-primary">Medication Details</h4>
              {patient.medication.medications.map((med, index) => (
                <Card key={index} className="border border-border">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <h4 className="text-primary">{med.name}</h4>
                        <p className="text-muted-foreground">{med.dosage} • {med.frequency}</p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`${getAdherenceBgColor((med.taken / med.scheduled) * 100)} ${getAdherenceColor((med.taken / med.scheduled) * 100)} ${getAdherenceBorderColor((med.taken / med.scheduled) * 100)}`}
                      >
                        {med.taken}/{med.scheduled} doses
                      </Badge>
                    </div>
                    
                    {med.missedDates.length > 0 && (
                      <div className="p-3 bg-[#FEEBDA] rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-[#F07D13] mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-[#F07D13] mb-1">Missed Doses</p>
                            <div className="flex flex-wrap gap-2">
                              {med.missedDates.map((date, idx) => (
                                <Badge key={idx} variant="outline" className="bg-white text-[#F07D13] border-[#F07D13]">
                                  {date}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Exercise Adherence Details */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Exercise Plan Adherence Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-[#e5e5e3] rounded-xl">
                <p className="text-muted-foreground">Assigned Exercises</p>
                <h3 className="text-primary">{patient.exercise.assignedExercises}</h3>
              </div>
              <div className="p-4 bg-[#D4F0F8] rounded-xl">
                <p className="text-muted-foreground">Completed Sessions</p>
                <h3 className="text-[#14CAF4]">{patient.exercise.completedExercises}</h3>
              </div>
              <div className="p-4 bg-[#FEEBDA] rounded-xl">
                <p className="text-muted-foreground">Missed Sessions</p>
                <h3 className="text-[#F07D13]">{patient.exercise.assignedExercises - patient.exercise.completedExercises}</h3>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="text-primary">Exercise Details</h4>
              {patient.exercise.exercises.map((exercise, index) => (
                <Card key={index} className="border border-border">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <h4 className="text-primary">{exercise.name}</h4>
                        <p className="text-muted-foreground">{exercise.type} • {exercise.scheduledPerWeek}x per week</p>
                        {exercise.lastCompleted && (
                          <p className="text-sm text-muted-foreground">Last completed: {exercise.lastCompleted}</p>
                        )}
                      </div>
                      <Badge 
                        variant="outline" 
                        className={`${getAdherenceBgColor((exercise.completedThisWeek / exercise.scheduledPerWeek) * 100)} ${getAdherenceColor((exercise.completedThisWeek / exercise.scheduledPerWeek) * 100)} ${getAdherenceBorderColor((exercise.completedThisWeek / exercise.scheduledPerWeek) * 100)}`}
                      >
                        {exercise.completedThisWeek}/{exercise.scheduledPerWeek} this week
                      </Badge>
                    </div>
                    
                    {exercise.missedDates.length > 0 && (
                      <div className="p-3 bg-[#FEEBDA] rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-[#F07D13] mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-[#F07D13] mb-1">Missed Sessions</p>
                            <div className="flex flex-wrap gap-2">
                              {exercise.missedDates.map((date, idx) => (
                                <Badge key={idx} variant="outline" className="bg-white text-[#F07D13] border-[#F07D13]">
                                  {date}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Home Test Adherence Details */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <FlaskConical className="w-5 h-5" />
              Home Test Adherence Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-[#e5e5e3] rounded-xl">
                <p className="text-muted-foreground">Scheduled Tests</p>
                <h3 className="text-primary">{patient.homeTest.scheduledTests}</h3>
              </div>
              <div className="p-4 bg-[#D4F0F8] rounded-xl">
                <p className="text-muted-foreground">Completed Tests</p>
                <h3 className="text-[#14CAF4]">{patient.homeTest.completedTests}</h3>
              </div>
              <div className="p-4 bg-[#FEEBDA] rounded-xl">
                <p className="text-muted-foreground">Missed Tests</p>
                <h3 className="text-[#F07D13]">{patient.homeTest.scheduledTests - patient.homeTest.completedTests}</h3>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h4 className="text-primary">Test Details</h4>
              {patient.homeTest.tests.map((test, index) => (
                <Card key={index} className="border border-border">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <h4 className="text-primary">{test.testName}</h4>
                        <p className="text-muted-foreground">{test.frequency}</p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                          {test.lastCompleted ? (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-[#14CAF4]" />
                              Last completed: {test.lastCompleted}
                            </p>
                          ) : (
                            <p className="text-sm text-[#F07D13] flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              Never completed
                            </p>
                          )}
                          <Separator orientation="vertical" className="hidden sm:block h-4" />
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Next scheduled: {test.nextScheduled}
                          </p>
                        </div>
                      </div>
                      {test.missedCount > 0 && (
                        <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]">
                          {test.missedCount} missed
                        </Badge>
                      )}
                    </div>
                    
                    {test.missedDates.length > 0 && (
                      <div className="p-3 bg-[#FEEBDA] rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-[#F07D13] mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-[#F07D13] mb-1">Missed Test Dates</p>
                            <div className="flex flex-wrap gap-2">
                              {test.missedDates.map((date, idx) => (
                                <Badge key={idx} variant="outline" className="bg-white text-[#F07D13] border-[#F07D13]">
                                  {date}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Doctor Notes Section */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Doctor's Notes & Action
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                Add your observations, recommendations, and action plan for this patient:
              </label>
              <textarea
                value={doctorNotes}
                onChange={(e) => setDoctorNotes(e.target.value)}
                className="w-full p-4 border border-border rounded-xl min-h-32 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Example: Reviewed adherence data. Patient showing poor medication compliance due to side effects. Discussed alternative medications. Encouraged to maintain exercise routine. Scheduled follow-up in 2 weeks to reassess."
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                variant="outline"
                onClick={handleSaveNotes}
                disabled={isSaving || !doctorNotes.trim()}
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Notes
              </Button>
              <Button
                onClick={handleMarkAsReviewed}
                disabled={isSaving || !doctorNotes.trim()}
                className="flex-1 bg-[#14CAF4] hover:bg-[#14CAF4]/90"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Mark as Reviewed & Remove from List'}
              </Button>
            </div>

            <div className="p-4 bg-[#D4F0F8] rounded-xl">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-[#2762AC] mt-0.5" />
                <div>
                  <p className="text-sm text-primary">
                    <strong>Note:</strong> Marking this patient as reviewed will remove them from the Low Adherence list. 
                    Your notes will be saved to the patient's record and can be viewed in their clinical history.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}